import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Auth, getIdToken, user } from "@angular/fire/auth";
import {
    CreateMttPayload,
    CreateSngPayload,
    CreateTablePayload,
    DeleteUserDataByProvider,
    EliminateFromGamePayload,
    FreePlayRingRequestPayload,
    FreePlaySngRequestPayload,
    GameServerStatus,
    IAcceptFriendRequestPayload,
    IAcceptFriendRequestResponse,
    IAdsBonusResponse,
    IDailyBonusResponse,
    IDenyFriendRequestPayload,
    IDenyFriendRequestResponse,
    IOnSetNFTAsAvatarPayload,
    IOnSettingsChangedPayload,
    ISendDirectMessage,
    ISendDirectMessageResponse,
    ISendFriendRequestPayload,
    ISendFriendRequestResponse,
    MainServerResponseSuccess,
    RequestJoinRINGPayload,
    RequestToDeleteTable,
    RequestToRegisterPayload,
    TableType,
    TradeRecordPayload,
    IGetLeaderboardPayload,
    LeaderboardItem,
    OneUserInfo,
    CheatMessagePayload,
    QuickplayRequest,
    MainServerResponseError,
    IAccepQuickPlayRequestPayload,
    IRejectQuickPlayRequestPayload,
    ITransferTableOwnershipPayload,
    IEditPlayerStackPayload,
    IPutPlayerOnStepAwayPayload,
    ISetQuickPlayTableProperties,
    IShuffleSeatsPayload,
    ITableResetPayload,
    IStartQuickplayTablePayload,
    IStopQuickplayTablePayload,
    IRemovePlayerFromQuickplayTablePayload,
    EditMttPayload,
    EditSngPayload,
    TransferTablePayload,
    AcceptInviteTransferTablePayload,
    RejectInviteTransferTablePayload,
    IStartQuickplayTournamentPayload,
    IRemovePlayerFromQuickplayTournamentPayload,
    ITransferTournamentOwnershipPayload,
    IQuickplaySetUserInfoPayload,
    RegisterBotPayload,
    AcceptAllRegisterTournamentPayload,
    TransferTournamentPayload,
    AcceptInviteTransferTournamentPayload,
    RejectInviteTransferTournamentPayload,
    TournamentDataType,
    WaitingListPayload,
    TournamentObjectType,
    IOnUserCreated,
    InviteCodesCreatePayload,
    InviteCodeCreateResponsePayload,
    GetDailyMissionsPayload,
    GetDailyMissionResponse,
    MissionCompletedPayload,
    ClaimCompletedMissionPayload,
    ILoginRewardResponse,
    IUserAccessResponse,
    ConvertXPPayload,
    IRetrieveCommunityFelts,
    ISaveFavouriteFeltPayload,
    IRemoveFavouriteFeltPayload,
    IPrizeClaimPayload,
    IProcessReceiptPayload,
    IProcessReceiptResponse,
    RequestToPauseTable,
    RequestToContinueTable,
    IMyriadCards,
    RequestToSitBot,
    IEditVerifiedUserDataPayload,
    IVerified1ClickResponse,
    IVerified1ClickPayload,
    ISavePhoneNumberPayload,
    TradeReportingPayload,
    ICoinflowDepositRequestPayload,
    ICoinflowIframeResponse,
    ICoinflowGetWithdrawerResponse,
    ICoinflowGetWithdrawerRequestPayload,
    ICoinflowAddPaymentReceiverRequestPayload,
    ICoinflowDeleteWithdrawerAccountRequestPayload,
    ICoinflowDefaultResponse,
    ICoinflowSendPayoutRequestPayload,
    IRedeemSweepbucksRequest,
    ISopoWalletShopPayload,
    IInitiateCryptoWithdrawPayload,
    IGetSeasonLeaderboardPayload,
    SeasonLeaderboardItem,
    IDeclineTransactionRequest,
    ITriggerMissionPayload,
    IAddUserRequestToGameQueuePayload,
    ICancelUserRequestInGameQueuePayload,
    ICoinflowPurchaseCheckoutLinkRequest,
    ICoinflowPurchaseCheckoutLinkResponse,
    IGetOneUserSeasonLeaderboardRankResult,
    IGetOneUserSeasonLeaderboardRankPayload,
} from "@soposopo/sopo-models";
import {
    CancelRequestOrUnregisterPayload,
    DeleteTournamentPayload,
} from "@soposopo/sopo-models/dist/socketPayload/TournamentHostPayload";
import { combineLatest, firstValueFrom, from, shareReplay, switchMap, take, timer } from "rxjs";
import { map } from "rxjs/operators";
import { ICustomerInfo, IDeleteFoundingSourcePayload, IFundingSourcePayload, ITransferRequestPayload } from "src/app/lobby/pages/cashier/components/redeem-sweepsbucks/components/standard-bank-transfer/standard-bank-transfer.component";
import { PicksPayload } from "src/app/messenger/pages/social-tabs/components/wager-selection/wager-selection.component";
import { environment } from "src/environments/environment";

@Injectable({
    providedIn: 'root'
})
export class ApiService {
    constructor (
        private http: HttpClient,
        private fireAuth: Auth,
    ) {}

    static resolveGameServerUrl(server: GameServerStatus) {
        if (server.state === 'Allocated' && server.externalAddress) return server.externalAddress;
        console.error('GS url not found!', server);
        throw new Error('GS url not found!');
    }

    createTable (payload: CreateTablePayload) {
        return this.postRequestPromise<typeof payload, string>(payload,'tables');
    }

    createTournament(payload: CreateSngPayload) {
        return this.postRequestPromise<typeof payload, string>(payload, `tournaments/${TableType.SNG}`);
    }

    createMtTournament(payload: CreateMttPayload) {
        return this.postRequestPromise<typeof payload, string>(payload, `tournaments/${TableType.MTT}`);
    }

    updateName (payload:  { userID: string; displayName: string }) {
        return this.postRequestPromise<typeof payload, void>(payload,'update-name');
    }

    //#region club

    getValidatedClubInfo(clubId: string) {
        return this.getRequest<MainServerResponseSuccess | MainServerResponseError>(`club/${clubId}/validate`);
    }

    callClubFunction<T extends string, P>(name: T, payload: P) {
        return this.postRequestPromise<typeof payload, any>(payload, `club/functions/${name}`);
    }

    public getMyriadCads() {
        return this.getRequest<IMyriadCards[]>(`myriad/markets`);
    }

    //#endregion

    joinWaitingList (payload: WaitingListPayload, url: string) {
        return this.postRequestPromise<typeof payload, void>(payload,'waiting-list/join', url);
    }

    leaveWaitingList (payload: WaitingListPayload, url: string) {
        return this.postRequestPromise<typeof payload, void>(payload,'waiting-list/leave', url);
    }

    requestTableJoin (payload: RequestJoinRINGPayload, url: string): Promise<MainServerResponseSuccess | MainServerResponseError> {
        return this.postRequestPromise<typeof payload, MainServerResponseSuccess | MainServerResponseError>(payload,'join-request', url);
    }

    cancelTableJoin (payload: RequestJoinRINGPayload, url: string): Promise<MainServerResponseSuccess | MainServerResponseError> {
        return this.postRequestPromise<typeof payload, MainServerResponseSuccess | MainServerResponseError>(payload,'join-request/cancel', url);
    }

    acceptTableJoin (payload: RequestJoinRINGPayload, url: string) {
        return this.postRequestPromise<typeof payload, void>(payload,'join-request/accept', url);
    }

    acceptAllTableJoin (payload: RequestJoinRINGPayload, url: string) {
        return this.postRequestPromise<typeof payload, void>(payload,'join-request/acceptall', url);
    }

    rejectTableJoin (payload: RequestJoinRINGPayload, url: string) {
        return this.postRequestPromise<typeof payload, void>(payload,'join-request/deny', url);
    }

    requestToRegister(payload: RequestToRegisterPayload, type: TableType, tournamentId: string) {
        //TODO inject firebase reporting  this.firebaseReporting.logEvent(ClientSocketEvent.REGISTER_TOURNAMENT, {userID, tournamentID});
        return this.postRequestPromise<typeof payload, void>(payload, `tournaments/${type}/${tournamentId}/schedule-register-request`);
    }

    requestToAcceptRegistration (userId: string, tournamentId: string, clubId: string, latitude: number, longitude: number, stack: number, myID: string, type: TableType) {
        const payload = {
            latitude,
            longitude,
            stack,
            userID: userId,
            clubId,
            myID
        }
        return this.postRequestPromise<typeof payload, void>(payload, `tournaments/${type}/${tournamentId}/schedule-register-request/accept`);
    }

    requestToRejectRegistration (userId: string, tournamentId: string, clubId: string, myID: string, type: TableType) {
        const payload = {
            userID: userId,
            clubId,
            myID
        }
        return this.postRequestPromise<typeof payload, void>(payload, `tournaments/${type}/${tournamentId}/schedule-register-request/deny`);
    }

    requestToAcceptAllRegistrationSNG (clubId: string, tournamentId: string, latitude: number, longitude: number, myID: string, type: TableType, joinRequestsIDs: string[]) {
        const payload: AcceptAllRegisterTournamentPayload = {
            latitude,
            longitude,
            clubId,
            joinRequestsIDs,
            myID
        }
        return this.postRequestPromise<typeof payload, void>(payload, `tournaments/${type}/${tournamentId}/schedule-register-request/acceptall`);
    }

    requestToAcceptAllRegistrationMTT (clubId: string, tournamentId: string, latitude: number, longitude: number, myID: string, type: TableType, joinRequestsIDs: string[]) {
        const payload: AcceptAllRegisterTournamentPayload = {
            latitude,
            longitude,
            clubId,
            joinRequestsIDs,
            myID,
        }
        return this.postRequestPromise<typeof payload, void>(payload, `tournaments/${type}/${tournamentId}/schedule-register-request/acceptall`);
    }

    sendBotNumber (payload: RegisterBotPayload, tournamentType: string) {
        return this.postRequestPromise<typeof payload, void>(payload, `spread_bots/${tournamentType}`);
    }

    cancelRegistrationRequest(payload: any, type: TableType, tournamentId: string) {
        return this.postRequestPromise<typeof payload, void>(payload, `tournaments/${type}/${tournamentId}/register-request/cancel`);
    }

    requestToUnRegister(payload: CancelRequestOrUnregisterPayload, type: TableType, tournamentId: string) {
        return this.postRequestPromise<typeof payload, void>(payload, `tournaments/${type}/${tournamentId}/unregister`);
    }

    onDeleteTournament(payload: DeleteTournamentPayload, type: TableType, tournamentId: string) {
        return this.postRequestPromise<typeof payload, void>(payload, `tournaments/${type}/${tournamentId}/delete`);
    }

    pauseTournament(tournamentId: string) {
        return this.postRequestPromise<{},  MainServerResponseSuccess | MainServerResponseError>({}, `/tournaments/${tournamentId}/pause`);
    }

    resumeTournament(tournamentId: string) {
        return this.postRequestPromise<{},  MainServerResponseSuccess | MainServerResponseError>({}, `tournaments/${tournamentId}/resume`);
    }

    requestToDeleteTable (payload: RequestToDeleteTable, url: string) {
        return this.postRequestPromise<typeof payload, void>(payload,'delete', url);
    }

    pauseRingGame (payload: RequestToPauseTable, url: string) {
        return this.postRequestPromise<typeof payload, void>(payload,'table/pause', url);
    }

    continueRingGame (payload: RequestToContinueTable, url: string) {
        return this.postRequestPromise<typeof payload, void>(payload,'table/continue', url);
    }

    requestToSit (payload: RequestToSitBot, url: string) {
        return this.postRequestPromise<typeof payload, void>(payload,'table/sit-bot', url);
    }

    eliminateFromJackpot(payload: EliminateFromGamePayload, type: TableType, tournamentId: string) {
        return this.postRequestPromise<typeof payload, void>(payload, `tournaments/${type}/${tournamentId}/eliminate-player`)
    }

    // selectBountyPlayers / /set-bounty-players endpoint removed by the MTT Bounty Suite.
    // Per-player bounty is now seeded at registration time on the backend.


    getFreePlayGame (payload: FreePlayRingRequestPayload | FreePlaySngRequestPayload) {
        return this.postRequestPromise<typeof payload, MainServerResponseSuccess>(payload, `free-play/${payload.type}`);
    }

    getFreePlayGameQueue(payload: IAddUserRequestToGameQueuePayload) {
        return this.postRequestPromise<typeof payload, MainServerResponseSuccess | MainServerResponseError>(payload, `free-play-queue/add-request`);
    }

    requestCancelQueue(payload: ICancelUserRequestInGameQueuePayload) {
        return this.postRequestPromise<typeof payload, MainServerResponseSuccess | MainServerResponseError>(payload, `free-play-queue/cancel-request`);
    }

    getTournamentData<T extends TournamentDataType, P extends TableType>(tournamentId: string, type: P, dataType: T){
        return this.getRequest<TournamentObjectType<T, P>>(`db/tournaments/${type}/${tournamentId}/${dataType}`);
    }

    startQuickplayMtt (payload: IStartQuickplayTournamentPayload, tournamentId: string) {
        return this.postRequestPromise<typeof payload, MainServerResponseSuccess | MainServerResponseError>(payload, `quick-play/${tournamentId}/start-tournament`);
    }

    removePlayerFromQuickplayTournament (payload: IRemovePlayerFromQuickplayTournamentPayload, type: string, tournamentId: string) {
        return this.postRequestPromise<typeof payload, MainServerResponseSuccess | MainServerResponseError>(payload, `quick-play/${type}/${tournamentId}/remove-player`);
    }

    transferTournamentOwnership(payload: ITransferTournamentOwnershipPayload, type: string, tournamentId: string) {
        return this.postRequestPromise<typeof payload, MainServerResponseSuccess | MainServerResponseError>(payload, `quick-play/${type}/${tournamentId}/transfer-ownership`);
    }

    transferQuickplayTournament(payload: TransferTournamentPayload, type: string, tournamentId: string) {
        return this.postRequestPromise<typeof payload, MainServerResponseSuccess | MainServerResponseError>(payload, `transfer-tournament/${type}/${tournamentId}`);
    }

    acceptQuickplayTournamentInvite(payload: AcceptInviteTransferTournamentPayload, type: string, tournamentId: string) {
        return this.postRequestPromise<typeof payload, MainServerResponseSuccess | MainServerResponseError>(payload, `transfer-tournament/${type}/${tournamentId}/accept-invite`);
    }

    rejectQucikplayTournamentInvite(payload: RejectInviteTransferTournamentPayload, type: string, tournamentId: string) {
        return this.postRequestPromise<typeof payload, MainServerResponseSuccess | MainServerResponseError>(payload, `transfer-tournament/${type}/${tournamentId}/reject-invite`);
    }

    getPlayersFromMtt(tournamentId: string) {
        return this.postRequestPromise<{}, MainServerResponseSuccess | MainServerResponseError>({}, `transfer-tournament/${TableType.MTT}/${tournamentId}/get-players`);
    }

    startQuickplayTable(payload: IStartQuickplayTablePayload, url: string) {
        return this.postRequestPromise<typeof payload, MainServerResponseSuccess | MainServerResponseError>(payload, `quick-play/start-table`, url);
    }

    stopQuickplayTable(payload: IStopQuickplayTablePayload, url: string) {
        return this.postRequestPromise<typeof payload, MainServerResponseSuccess | MainServerResponseError>(payload, `quick-play/stop-table`, url);
    }

    setQuickPlayTableProperties(payload: ISetQuickPlayTableProperties, url: string) {
        return this.postRequestPromise<typeof payload, MainServerResponseSuccess | MainServerResponseError>(payload, `quick-play/set-table-properties`, url);
    }

    requestJoinQuickPlay(payload: QuickplayRequest, url: string) {
        return this.postRequestPromise<typeof payload, MainServerResponseSuccess | MainServerResponseError>(payload, `quick-play/join-request`, url);
    }

    acceptQuickPlayRequest(payload: IAccepQuickPlayRequestPayload, url: string) {
        return this.postRequestPromise<typeof payload, MainServerResponseSuccess | MainServerResponseError>(payload,`quick-play/join-request/accept`, url);
    }

    denyQuickPlayRequest(payload: IRejectQuickPlayRequestPayload, url: string) {
        return this.postRequestPromise<typeof payload, MainServerResponseSuccess | MainServerResponseError>(payload, `quick-play/join-request/deny`, url);
    }

    transferQuickPlayTableOwnership(payload: ITransferTableOwnershipPayload, url: string) {
        return this.postRequestPromise<typeof payload, MainServerResponseSuccess | MainServerResponseError>(payload, `quick-play/transfer-table-ownership`, url);
    }

    editQuickPlayPlayersStack(payload: IEditPlayerStackPayload, url: string) {
        return this.postRequestPromise<typeof payload, MainServerResponseSuccess | MainServerResponseError>(payload, `quick-play/edit-player-stack`, url);
    }

    setQuickPlayPlayerToSteppedAway(payload: IPutPlayerOnStepAwayPayload, url: string) {
        return this.postRequestPromise<typeof payload, MainServerResponseSuccess | MainServerResponseError>(payload, `quick-play/set-player-to-stepped-away`, url);
    }

    removePlayerFromQuickplayTable(payload: IRemovePlayerFromQuickplayTablePayload, url: string) {
        return this.postRequestPromise<typeof payload, MainServerResponseSuccess | MainServerResponseError>(payload, `quick-play/remove-player`, url);
    }

    shuffleQuickPlaySeats(payload: IShuffleSeatsPayload, url: string) {
        return this.postRequestPromise<typeof payload, MainServerResponseSuccess | MainServerResponseError>(payload, `quick-play/shuffle-seats`, url);
    }

    resetQuickPlayTable(payload: ITableResetPayload, url: string) {
        return this.postRequestPromise<typeof payload, MainServerResponseSuccess | MainServerResponseError>(payload, `quick-play/reset-table`, url);
    }

    requestTradeRecord (payload: TradeRecordPayload | TradeReportingPayload) {
        return this.postRequestPromise<typeof payload, MainServerResponseSuccess>(payload, 'request-trade-record');
    }

    deleteUser(payload: DeleteUserDataByProvider) {
        return this.postRequestPromise<typeof payload, MainServerResponseSuccess>(payload, 'delete-user');
    }

    deleteUserData(payload: DeleteUserDataByProvider) {
        return this.postRequestPromise<typeof payload, MainServerResponseSuccess>(payload, 'delete-user-data');
    }

    sendFriendRequest(payload: ISendFriendRequestPayload) {
        return this.postRequestPromise<ISendFriendRequestPayload, ISendFriendRequestResponse>(payload, 'messenger/sendFriendRequest');
    }

    acceptFriendRequest(payload: IAcceptFriendRequestPayload) {
        return this.postRequestPromise<IAcceptFriendRequestPayload, IAcceptFriendRequestResponse>(payload, 'messenger/acceptFriendRequest');
    }

    denyFriendRequest(payload: IDenyFriendRequestPayload) {
        return this.postRequestPromise<IDenyFriendRequestPayload, IDenyFriendRequestResponse>(payload, 'messenger/denyFriendRequest');
    }

    sendDirectMessage(payload: ISendDirectMessage) {
        return this.postRequestPromise<ISendDirectMessage, ISendDirectMessageResponse>(payload, 'messenger/sendDirectMessage');
    }

    onCheckNftOnwership(userId: string) {
        return this.postRequestPromise<Object, void>({}, `crypto/${userId}/onCheckNftOnwership`);
    }

    onSetNftAsAvatar(payload: IOnSetNFTAsAvatarPayload, userId: string) {
        return this.postRequestPromise<IOnSetNFTAsAvatarPayload, void>(payload, `crypto/${userId}/onSetNftAsAvatar`);
    }

    convertedXP(payload: ConvertXPPayload) {
        return this.postRequestPromise<ConvertXPPayload, void>(payload, 'user/schedule-xpConverted');
    }

    processReceipt(payload: IProcessReceiptPayload) {
        return this.postRequestPromise<IProcessReceiptPayload, IProcessReceiptResponse>(payload, 'purchase/processReceipt');
    }

    adsBonus(userId: string) {
        return this.postRequestPromise<Object, IAdsBonusResponse>({}, `user/${userId}/adsBonus`);
    }

    dailyBonus(userId: string, sweepbucks: boolean): Promise<IDailyBonusResponse> {
        return this.postRequestPromise<Object, IDailyBonusResponse>({}, `user/${userId}/dailyBonus?sweepbucks=${sweepbucks ? true : false}`);
    }

    getSopoPlusLeaderboard(payload: IGetLeaderboardPayload) {
        return this.postRequestPromise<IGetLeaderboardPayload, LeaderboardItem[]>(payload, 'sopoplus/getLeaderboardData');
    }

    getOneUserSopoPlusScore(userID: string) {
        return this.getRequest<OneUserInfo>(`sopoplus/${userID}/getOneUserPoints`);
    }

    getOneUserSeasonLeaderboardRank(payload: IGetOneUserSeasonLeaderboardRankPayload) {
        return this.postRequestPromise<IGetOneUserSeasonLeaderboardRankPayload, IGetOneUserSeasonLeaderboardRankResult>(payload, 'get-one-user-season-leaderboard-rank');
    }

    getSeasonLeaderboard(payload: IGetSeasonLeaderboardPayload) {
        return this.postRequestPromise<IGetSeasonLeaderboardPayload, SeasonLeaderboardItem[]>(payload, 'get-season-leaderboard');
    }

    onSettingsChanged(payload: IOnSettingsChangedPayload, userId: string) {
        return this.postRequestPromise<IOnSettingsChangedPayload, void>(payload, `user/${userId}/onSettingsChanged`);
    }

    bypassSubscription(userId: string) {
        return this.postRequestPromise<{}, void>({}, `purchase/bypass/${userId}`);
    }

    editTournament(payload: EditMttPayload | EditSngPayload, type: TableType, tournamentId: string) {
        return this.postRequestPromise<typeof payload, MainServerResponseSuccess>(payload, `tournaments/${type}/${tournamentId}/edit`);
    }

    transferTable(payload: TransferTablePayload, tableId: string, url: string) {
        return this.postRequestPromise<typeof payload, TransferTablePayload>(payload, `transfer-table/${tableId}`, url);
    }

    transferTableAcceptInvite(payload: AcceptInviteTransferTablePayload, tableId: string, url: string) {
        return this.postRequestPromise<typeof payload, AcceptInviteTransferTablePayload>(payload, `transfer-table/${tableId}/accept-invite`, url);
    }

    transferTableRejectInvite(payload: RejectInviteTransferTablePayload, tableId: string, url: string) {
        return this.postRequestPromise<typeof payload, RejectInviteTransferTablePayload>(payload, `transfer-table/${tableId}/reject-invite`, url);
    }

    onCheatMessage(payload: CheatMessagePayload) {
        return this.postRequestPromise<typeof payload, void>(payload, `messages/cheat`);
    }

    setTournamentUserInfo(payload: IQuickplaySetUserInfoPayload, type: TableType, tournamentId: string) {
        return this.postRequestPromise<typeof payload, void>(payload, `quick-play/${type}/${tournamentId}/set-userInfo`);
    }

    notifyBEOnUserCreated(payload: IOnUserCreated) {
        return this.postRequestPromise<typeof payload, MainServerResponseSuccess>(payload, `user/user-created`);
    }

    saveInviteCode(payload: InviteCodesCreatePayload): Promise<InviteCodeCreateResponsePayload> {
        return this.postRequestPromise<typeof payload, InviteCodeCreateResponsePayload>(payload, `user/saveUserInviteCode`);
    }

    redeemXpPrize(payload: IPrizeClaimPayload): Promise<MainServerResponseSuccess> {
        return this.postRequestPromise<typeof payload, MainServerResponseSuccess>(payload, `user/claimPrize`);
    }

    getNewUserAccess(userId: string): Promise<IUserAccessResponse> {
        return this.postRequestPromise<{}, IUserAccessResponse>({}, `user/${userId}/access`);
    }

    getLoginStreakReward(userId: string): Promise<ILoginRewardResponse> {
        return this.postRequestPromise<{}, ILoginRewardResponse>({}, `user/${userId}/claimReward`);
    }

    //#region CommunityFelts
    getCommunityFelts(payload: IRetrieveCommunityFelts): Promise<any> {
        return this.postRequestPromise<typeof payload, any>(payload, 'retrieve-community-felts');
    }

    saveFavoriteCommunityFelts(payload: ISaveFavouriteFeltPayload): Promise<void> {
        return this.postRequestPromise<typeof payload, any>(payload, 'save-favourite-felt');
    }

    removeFavoriteCommunityFelts(payload: IRemoveFavouriteFeltPayload): Promise<void> {
        return this.postRequestPromise<typeof payload, any>(payload, 'remove-favourite-felt');
    }
    //#endregion CommunityFelts

    getDailyMissions(payload: GetDailyMissionsPayload): Promise<GetDailyMissionResponse> {
        return this.postRequestPromise<typeof payload, GetDailyMissionResponse>(payload, 'user/getDailyMissions');
    }

    missionCompleted(payload: MissionCompletedPayload): Promise<any> {
        return this.postRequestPromise<typeof payload, any>(payload, 'user/missionCompleted');
    }

    missionClaimed(payload: ClaimCompletedMissionPayload): Promise<any> {
        return this.postRequestPromise<typeof payload, any>(payload, 'user/missionClaimed');
    }

    triggerMission(payload: ITriggerMissionPayload) {
        return this.postRequestPromise<typeof payload, any>(payload, 'trigger/mission');
    }

    notifyBEOnUserAccess(userId: string) {
        return this.postRequestPromise({}, `user/${userId}/log-user-access`)
    }

    async getServerTime () {
        let serverTime = 0;
        try {
            const response = await this.getRequest<MainServerResponseSuccess>('server-time')
            serverTime = response.result['serverTime'];
        } catch (error) {
            console.error('Error getting server-time:', error)
            serverTime = (new Date()).getTime() / 1000;
        }
        return serverTime;
    }

    getServerTimeObservable() {
        return combineLatest([
            from(this.getServerTime()).pipe(map(serverTime=>{
                    const dateTime = Math.floor((new Date()).getTime()/1000);
                    const offset = serverTime - dateTime;
                    return offset
                }),
                shareReplay({ bufferSize: 1, refCount: true })
            ),
            timer(0, 1000)
        ]).pipe(
            map(([offset, _count]) => Math.floor(Date.now() / 1000) + offset)
        )
    }

    createCustumer(payload: ICustomerInfo, userId: string) {
        return this.postRequestPromise<typeof payload, any>(payload, `payout/${userId}/create`);
    }

    addFoundingSource(payload: IFundingSourcePayload, userId: string) {
        return this.postRequestPromise<typeof payload, any>(payload, `payout/${userId}/add-source`);
    }

    createTransfer(payload: ITransferRequestPayload, userId: string) {
        return this.postRequestPromise<typeof payload, any>(payload, `payout/${userId}/transfer`);
    }

    deleteFundingSource(payload: IDeleteFoundingSourcePayload, userId: string): Promise<any> {
        return this.postRequestPromise<typeof payload, any>(payload, `payout/${userId}/delete-source`);
    }

    // #region Verification
    savePhoneNumber(userId: string, payload: ISavePhoneNumberPayload) {
        return this.postRequestPromise<typeof payload, MainServerResponseSuccess>(payload, `user/${userId}/save-phone-number`);
    }

    verified1Click(payload: IVerified1ClickPayload) {
        return this.postRequestPromise<IVerified1ClickPayload, IVerified1ClickResponse>(payload, `verified/1-click`);
    }

    setUserEmail(userId: string) {
        return this.postRequestPromise<Object, void>({}, `user/${userId}/set-user-email`);
    }

    setUserEmailVerified(userId: string) {
        return this.postRequestPromise<Object, void>({}, `user/${userId}/set-user-email-verified`);
    }

    setUserVerifiedDataStatusAccepted(userId: string) {
        return this.postRequestPromise<Object, void>({}, `verified/set-status-accepted/${userId}`);
    }

    setUserVerifiedDataStatusCanceled(userId: string) {
        return this.postRequestPromise<Object, void>({}, `verified/set-status-canceled/${userId}`);
    }

    editVerifiedUserData(payload: IEditVerifiedUserDataPayload, userId: string) {
        return this.postRequestPromise<IEditVerifiedUserDataPayload, void>(payload, `verified/edit-user-data/${userId}`);
    }

    // #endregion Verification

    // #region Coinflow

    public coinflowDeposit(payload: ICoinflowDepositRequestPayload): Promise<ICoinflowIframeResponse> {
        return this.postRequestPromise<typeof payload, ICoinflowIframeResponse>(payload, `coinflow/checkout-link`);
    }

    public coinflowGetWithdrawer(payload: ICoinflowGetWithdrawerRequestPayload): Promise<ICoinflowGetWithdrawerResponse> {
        return this.postRequestPromise<typeof payload, ICoinflowGetWithdrawerResponse>(payload, `coinflow/get-withdrawer`);
    }

    public coinflowAddPaymentReceiver(payload: ICoinflowAddPaymentReceiverRequestPayload): Promise<ICoinflowIframeResponse> {
        return this.postRequestPromise<typeof payload, ICoinflowIframeResponse>(payload, `coinflow/add-payment-receiver`);
    }

    public coinflowDeleteWithdrawerAccount(payload: ICoinflowDeleteWithdrawerAccountRequestPayload): Promise<ICoinflowDefaultResponse> {
        return this.postRequestPromise<typeof payload, ICoinflowDefaultResponse>(payload, `coinflow/delete-withdraw-account`);
    }

    public coinflowSendPayout(payload: ICoinflowSendPayoutRequestPayload): Promise<ICoinflowDefaultResponse> {
        return this.postRequestPromise<typeof payload, ICoinflowDefaultResponse>(payload, `coinflow/initiate-payout`);
    }

    public coinflowKYC(userId: string): Promise<ICoinflowDefaultResponse> {
        return this.getRequest<ICoinflowDefaultResponse>(`coinflow/kyc/${userId}`);
    }

    public declineTransaction(payload: IDeclineTransactionRequest): Promise<any> {
        return this.postRequestPromise<IDeclineTransactionRequest, any>(payload, 'decline-payout');
    }

    /**
     * Generate Coinflow checkout link for webview purchases
     */
    public coinflowPurchaseCheckoutLink(payload: ICoinflowPurchaseCheckoutLinkRequest): Promise<ICoinflowPurchaseCheckoutLinkResponse> {
        return this.postRequestPromise<ICoinflowPurchaseCheckoutLinkRequest, ICoinflowPurchaseCheckoutLinkResponse>(payload, `coinflow/purchase-checkout-link`);
    }

    // #endregion Coinflow

    //#region Redeem Sweepbucks

    public redeemSweepsbucks(payload: IRedeemSweepbucksRequest): Promise<ICoinflowDefaultResponse> {
        return this.postRequestPromise<typeof payload, ICoinflowDefaultResponse>(payload, `/redeem-sweepsbucks`);
    }

    //#endregion Redeem Sweepbucks

    //#region SopoWallet purchase

    public sopoWalletPurchase(payload: ISopoWalletShopPayload): Promise<ICoinflowDefaultResponse> {
        return this.postRequestPromise<typeof payload, ICoinflowDefaultResponse>(payload, `/sopowallet-purchase`);
    }

    //#endregion SopoWallet purchase

    //#region Crypto Withdraw

    public cryptoWithdraw(payload: IInitiateCryptoWithdrawPayload): Promise<ICoinflowDefaultResponse> {
        return this.postRequestPromise<typeof payload, ICoinflowDefaultResponse>(payload, `/initiate-crypto-withdraw`);
    }

    //#endregion Crypto Withdraw

    //#region Zendesk

    public getLoginToken(userId: string): Promise<MainServerResponseSuccess | MainServerResponseError> {
        return this.getRequest<MainServerResponseSuccess | MainServerResponseError>(`zendesk/get-login-token/${userId}`);
    }

    public createOrUptadeEndUser(payload: {userId: string}): Promise<MainServerResponseSuccess | MainServerResponseError> {
        return this.postRequestPromise<typeof payload, MainServerResponseSuccess | MainServerResponseError>(payload, `/zendesk/create-or-update-end-user`);
    }

    //#endregion Zendesk

    //#region POST
    /**
     * You need to declare PAYLOAD and RESPONSE type
    */
    postRequestPromise<PAYLOAD,RESPONSE>(payload: PAYLOAD, endPointUrl: string, baseUrl = environment.apiUrl): Promise<any> {
        return firstValueFrom(this.postRequest<PAYLOAD,RESPONSE>(payload,endPointUrl, baseUrl));
    }

    /**
     * You need to declare PAYLOAD and RESPONSE type
     */
    postRequest<PAYLOAD,RESPONSE>(payload: PAYLOAD, endPointUrl: string, baseUrl: string) {
        return user(this.fireAuth).pipe(
            take(1),
            switchMap(async userRef => {
                if (userRef === null) return null;
                const token = await getIdToken(userRef);
                return token;
            }),
            switchMap(token => {
                const headers = new HttpHeaders({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` });
                return this.http.post<RESPONSE>( baseUrl + endPointUrl, payload, { headers })
            })
        );
    }
    //#endregion

    //#region GET
    /**
     * You need to declare PAYLOAD and RESPONSE type
     */
    getRequest<RESPONSE>(endPointUrl: string) {
        const reuqest$ = user(this.fireAuth).pipe(
            take(1),
            switchMap(async userRef => {
                if (userRef === null) return null;
                const token = await getIdToken(userRef);
                return token;
            }),
            switchMap(token => {
                const headers = new HttpHeaders({ 'Authorization': `Bearer ${token}` });
                return this.http.get<RESPONSE>( environment.apiUrl + endPointUrl, { headers });
            })
        );
        return firstValueFrom(reuqest$);
    }
    //#endregion

    //#region Picks
    createPicks(payload: PicksPayload) {
        console.log('api service picks');
        return this.postRequestPromise<typeof payload, any>(payload, `picks/create`);
    }

    getAllPicks() {
        return this.getRequest<MainServerResponseSuccess>(`picks`);
    }

    //#endregion Picks

    //#region Stakings
    getAllStakings() {
        return this.getRequest<MainServerResponseSuccess>(`stakings`);
    }
    //#endregion Stakings
}
